import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { ChevronLeft, ChevronDown, Star, Heart, Share2, ShoppingCart, MessageCircle, Truck, ShieldCheck, ThumbsUp, Tag, Package } from "lucide-react";
import { useCart } from "./StoreLayout";
import { toast } from "sonner";

const WA_CS = "6281234567890";
const COLORS = [
  { name: "Pink", hex: "#F3C6CE" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Blue", hex: "#C7D2E8" },
  { name: "Black", hex: "#2B2B2B" },
];
const SIZES = ["S", "M", "L", "XL"];

function AccRow({ title, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-blush-soft last:border-0">
      <button data-testid={testid} onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-3.5 text-sm font-semibold text-cocoa">
        {title}
        <ChevronDown className={`w-4 h-4 text-cocoa-muted transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-4 text-xs text-cocoa-muted leading-relaxed space-y-1.5">{children}</div>}
    </div>
  );
}

export default function StoreProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState("Pink");
  const [size, setSize] = useState("M");
  const [thumb, setThumb] = useState(0);
  const [reviews, setReviews] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [claimed, setClaimed] = useState(() => localStorage.getItem("kiyana_claimed_voucher") || "");
  const [showAllRev, setShowAllRev] = useState(false);
  const [helped, setHelped] = useState(() => { try { return JSON.parse(localStorage.getItem("kiyana_helped") || "[]"); } catch { return []; } });
  const { cart, addToCart, toggleWish, wishlist } = useCart();
  const isWish = wishlist.find((w) => w.id === id);

  useEffect(() => { api.get(`/products/${id}`).then((r) => setP(r.data)); }, [id]);
  useEffect(() => { api.get("/vouchers").then((r) => setVouchers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!p) return;
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/products", { params: { category: p.category } }).then((r) => setSimilar(r.data.filter((x) => x.id !== id).slice(0, 6))).catch(() => {});
  }, [p, id]);

  if (!p) return <div className="py-20 text-center text-cocoa-muted text-sm">Memuat...</div>;

  const gallery = [p.image, p.image, p.image, p.image];
  const effPrice = p.promo_price ?? p.price;
  const grosirTiers = [...(p.grosir_tiers || [])].sort((a, b) => b.min_qty - a.min_qty);
  const activeTier = grosirTiers.find((t) => qty >= t.min_qty);
  const unitNow = activeTier ? Math.round(effPrice * (1 - activeTier.percent / 100)) : effPrice;
  const sub = unitNow * qty;
  const claimedV = vouchers.find((v) => v.code === claimed);
  const claimedDisc = claimedV && sub >= (claimedV.min_purchase || 0)
    ? Math.min(Math.floor(sub * claimedV.percent / 100), claimedV.max_discount || Infinity) : 0;
  const displayTotal = sub - claimedDisc;
  const soldTxt = (p.sold || 0) >= 1000 ? ((p.sold / 1000).toFixed(1) + "K") : (p.sold || 0);

  const claimVoucher = (v) => {
    localStorage.setItem("kiyana_claimed_voucher", v.code);
    setClaimed(v.code);
    toast.success(`Voucher ${v.code} aktif! Otomatis terpasang saat checkout.`);
  };

  const markHelpful = async (rid) => {
    if (helped.includes(rid)) return;
    try {
      await api.post(`/reviews/${rid}/helpful`);
      const next = [...helped, rid];
      setHelped(next);
      localStorage.setItem("kiyana_helped", JSON.stringify(next));
      setReviews((r) => ({ ...r, items: r.items.map((x) => x.id === rid ? { ...x, helpful: (x.helpful || 0) + 1 } : x) }));
    } catch { /* abaikan */ }
  };

  const share = async () => {
    try {
      await navigator.share?.({ title: p.name, url: window.location.href });
    } catch {
      navigator.clipboard?.writeText(window.location.href);
      toast.success("Link produk disalin");
    }
  };

  const buyNow = () => { addToCart({ ...p, price: effPrice }, qty, `${color}, ${size}`); nav("/store/checkout"); };

  return (
    <div className="animate-fade-up -mx-4 -mt-4 pb-28">
      {/* Gallery */}
      <div className="relative">
        <div className="aspect-[4/5] bg-white">
          <img src={gallery[thumb]} alt={p.name} className="w-full h-full object-cover" />
        </div>
        <button onClick={() => nav(-1)} data-testid="detail-back-btn" className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/30 backdrop-blur flex items-center justify-center">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <div className="absolute top-4 right-4 flex gap-2">
          <button data-testid="detail-share-btn" onClick={share} className="w-9 h-9 rounded-full bg-black/30 backdrop-blur flex items-center justify-center">
            <Share2 className="w-4 h-4 text-white" />
          </button>
          <button data-testid="detail-cart-btn" onClick={() => nav("/store/cart")} className="relative w-9 h-9 rounded-full bg-black/30 backdrop-blur flex items-center justify-center">
            <ShoppingCart className="w-4 h-4 text-white" />
            {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-brand text-white text-[9px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">{cart.length}</span>}
          </button>
        </div>
        <div className="absolute bottom-3 right-3 bg-black/50 text-white text-[10px] rounded-full px-2 py-0.5">{thumb + 1}/{gallery.length}</div>
      </div>

      <div className="px-4">
        <div className="flex gap-2 py-3">
          {gallery.map((im, i) => (
            <button key={i} onClick={() => setThumb(i)} className={`w-14 h-14 rounded-xl overflow-hidden border-2 ${thumb === i ? "border-brand" : "border-blush-soft"}`}>
              <img src={im} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        {/* Harga + voucher */}
        <div className="flex items-end gap-2 flex-wrap">
          <span data-testid="detail-price" className="text-brand text-2xl font-bold leading-none">{formatIDR(displayTotal)}</span>
          {(claimedDisc > 0 || effPrice < p.price) && <span className="text-xs text-cocoa-muted line-through">{formatIDR(p.price * qty)}</span>}
          {effPrice < p.price && <span className="text-[10px] text-red-500 font-bold">-{Math.round(p.promo_percent)}%</span>}
          {claimedV && claimedDisc > 0 && (
            <span data-testid="detail-voucher-tag" className="text-[9px] bg-brand text-white font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
              <Tag className="w-2.5 h-2.5" /> Dengan Voucher {claimedV.code}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-cocoa-muted mt-1.5">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          <span className="text-cocoa font-medium">{reviews?.avg ?? p.rating ?? "-"}</span>
          <span>({reviews?.count ?? 0} penilaian)</span>
          <span>|</span>
          <span data-testid="detail-sold">Terjual {soldTxt}</span>
          <button data-testid="detail-wish-btn" onClick={() => toggleWish(p)} className="ml-auto">
            <Heart className={`w-5 h-5 ${isWish ? "fill-brand text-brand" : "text-cocoa-muted"}`} />
          </button>
        </div>

        {/* Voucher chips — klik langsung aktif */}
        {vouchers.length > 0 && (
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
            {vouchers.map((v) => {
              const active = claimed === v.code;
              return (
                <button key={v.code} data-testid={`voucher-chip-${v.code}`} onClick={() => claimVoucher(v)}
                  className={`flex-shrink-0 rounded-lg border px-3 py-1.5 text-left text-[10px] font-semibold ${active ? "bg-brand text-white border-brand" : "bg-blush text-brand border-blush-soft"}`}>
                  {v.percent}% OFF • Min. {formatIDR(v.min_purchase)}
                  <span className={`block font-normal ${active ? "text-white/80" : "text-cocoa-muted"}`}>{active ? "Aktif ✓" : "Klik untuk aktifkan"}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Nama produk */}
        <h1 className="font-medium text-[15px] leading-snug mt-3">
          <span className="text-[9px] bg-brand text-white font-bold px-1.5 py-0.5 rounded mr-1.5 align-middle">Official</span>
          {p.name}
        </h1>
        <div className="text-[11px] text-teal-600 mt-1">Stok tersedia: {p.stock}</div>
        {(p.bundle_min_qty || 0) > 0 && (p.bundle_percent || 0) > 0 && (
          <div data-testid="bundle-info" className="mt-2 bg-blush border border-brand/30 rounded-xl px-3 py-2 text-[11px] text-brand font-medium">
            Promo Bundle: beli {p.bundle_min_qty} pcs atau lebih, hemat {p.bundle_percent}% per pcs — otomatis saat checkout.
          </div>
        )}

        {/* Estimasi kirim */}
        <div className="mt-4 bg-white rounded-2xl border border-blush-soft p-3.5 flex items-center gap-2.5">
          <Truck className="w-5 h-5 text-teal-600 flex-shrink-0" />
          <div className="text-xs">
            <div className="font-semibold text-teal-700">Estimasi tiba 2-4 hari</div>
            <div className="text-[10px] text-cocoa-muted">Dikirim dari Kota Cirebon • subsidi ongkir Rp15rb untuk belanja ≥ Rp150rb</div>
          </div>
        </div>

        {/* Warna */}
        <div className="mt-5">
          <div className="text-sm font-semibold mb-2">Warna</div>
          <div className="flex gap-3">
            {COLORS.map((c) => (
              <button key={c.name} data-testid={`color-${c.name}`} onClick={() => setColor(c.name)} className="flex flex-col items-center gap-1">
                <div className={`w-14 h-14 rounded-xl overflow-hidden border-2 relative ${color === c.name ? "border-brand" : "border-blush-soft"}`}>
                  <img src={p.image} alt={c.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 text-center text-[9px] font-semibold py-0.5" style={{ background: c.hex, color: c.name === "Black" ? "#fff" : "#5C4033" }}>{c.name}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Ukuran */}
        <div className="mt-5">
          <div className="text-sm font-semibold mb-2">Ukuran</div>
          <div className="flex gap-2">
            {SIZES.map((s) => (
              <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                className={`w-12 h-10 rounded-full border text-sm font-medium ${size === s ? "bg-brand text-white border-brand" : "bg-white border-blush-soft"}`}>{s}</button>
            ))}
          </div>
        </div>

        {/* Jumlah */}
        <div className="mt-5 flex items-center gap-3">
          <span className="text-sm font-semibold">Jumlah</span>
          <div className="flex items-center border border-blush-soft rounded-full bg-white">
            <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 text-lg">−</button>
            <div className="w-10 text-center font-medium text-sm">{qty}</div>
            <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="w-10 h-10 text-lg">+</button>
          </div>
        </div>

        {/* Harga Grosir */}
      {grosirTiers.length > 0 && (
        <div data-testid="grosir-box" className="mt-5 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
          <div className="text-sm font-bold text-emerald-700 flex items-center gap-1.5"><Package className="w-4 h-4" /> Harga Grosir — makin banyak, makin hemat!</div>
          <div className="mt-2 space-y-1.5">
            {[...grosirTiers].sort((a, b) => a.min_qty - b.min_qty).map((t) => {
              const on = qty >= t.min_qty;
              const tp = Math.round(effPrice * (1 - t.percent / 100));
              return (
                <div key={t.min_qty} data-testid={`grosir-tier-${t.min_qty}`} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs ${on ? "bg-emerald-600 text-white font-bold" : "bg-white/70 text-cocoa"}`}>
                  <span>Beli {t.min_qty}+ pcs {on && "✓ aktif"}</span>
                  <span>{formatIDR(tp)}/pcs <span className={on ? "text-emerald-100" : "text-emerald-600"}>(-{t.percent}%)</span></span>
                </div>
              );
            })}
          </div>
          {activeTier ? (
            <div data-testid="grosir-saving" className="mt-2 text-[11px] font-bold text-emerald-700">Kamu hemat {formatIDR((effPrice - unitNow) * qty)} untuk {qty} pcs!</div>
          ) : (
            <div className="mt-2 text-[10px] text-cocoa-muted">Tambah {[...grosirTiers].sort((a, b) => a.min_qty - b.min_qty)[0].min_qty - qty} pcs lagi untuk dapat harga grosir.</div>
          )}
        </div>
      )}

      {/* Info produk */}
        <div className="mt-6 bg-white rounded-2xl border border-blush-soft px-4">
          <AccRow title="Panduan Ukuran" testid="acc-size">
            <p><b className="text-cocoa">Panduan ukuran (LD × PB):</b> S: 90×58 • M: 96×60 • L: 102×62 • XL: 108×64 cm. Model menggunakan ukuran M.</p>
          </AccRow>
          <AccRow title="Deskripsi" testid="acc-desc">
            <p>{p.description}</p>
            <p>Bahan premium pilihan, adem dan tidak menerawang. Perawatan: cuci tangan / mesin delicate, jangan disetrika terlalu panas.</p>
          </AccRow>
          <AccRow title="Jaminan Kiyana" testid="acc-guarantee">
            <p className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-teal-600" /> 100% produk original Kiyana by Cirebon Cloth.</p>
            <p>• Retur mudah jika produk cacat/tidak sesuai (sertakan bukti foto/video).</p>
            <p>• Garansi tiba: voucher Rp2.000 jika pesanan lewat dari estimasi.</p>
          </AccRow>
        </div>

        {/* Penilaian Produk */}
        <div className="mt-4 bg-white rounded-2xl border border-blush-soft p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span data-testid="review-avg" className="text-xl font-bold text-cocoa">{reviews?.avg ?? "-"}</span>
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              <span className="text-sm font-semibold">Penilaian Produk ({reviews?.count ?? 0})</span>
            </div>
            {(reviews?.count || 0) > 3 && (
              <button data-testid="review-see-all" onClick={() => setShowAllRev(!showAllRev)} className="text-xs text-brand font-medium">
                {showAllRev ? "Tutup" : "Lihat Semua ›"}
              </button>
            )}
          </div>
          {!reviews && <div className="text-xs text-cocoa-muted">Memuat penilaian...</div>}
          {reviews?.count === 0 && <div className="text-xs text-cocoa-muted py-3 text-center">Belum ada penilaian. Jadilah yang pertama setelah membeli produk ini!</div>}
          {(showAllRev ? reviews?.items : reviews?.items?.slice(0, 3))?.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="py-3 border-b border-blush-soft last:border-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blush flex items-center justify-center text-[11px] font-bold text-brand">{(r.user_name || "P")[0].toUpperCase()}</div>
                <div className="text-xs font-medium">{r.user_name}</div>
                <button data-testid={`review-helpful-${r.id}`} onClick={() => markHelpful(r.id)}
                  className={`ml-auto text-[10px] flex items-center gap-1 ${helped.includes(r.id) ? "text-brand font-semibold" : "text-cocoa-muted"}`}>
                  Membantu ({r.helpful || 0}) <ThumbsUp className={`w-3 h-3 ${helped.includes(r.id) ? "fill-brand/20" : ""}`} />
                </button>
              </div>
              <div className="text-[10px] text-amber-500 mt-1">{"★".repeat(r.rating)}<span className="text-blush-soft">{"★".repeat(5 - r.rating)}</span></div>
              {r.comment && <div className="text-xs text-cocoa mt-1 leading-relaxed">{r.comment}</div>}
              {r.reply && (
                <div data-testid={`review-seller-reply-${r.id}`} className="mt-2 ml-2 rounded-lg bg-blush/60 border border-blush-soft p-2.5">
                  <div className="text-[10px] font-bold text-brand">Balasan Penjual</div>
                  <div className="text-[11px] text-cocoa mt-0.5 leading-relaxed">{r.reply.text}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Produk serupa */}
        {similar.length > 0 && (
          <div className="mt-5">
            <div className="text-sm font-semibold mb-2">Produk Serupa</div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
              {similar.map((s) => (
                <button key={s.id} data-testid={`similar-${s.id}`} onClick={() => { nav(`/store/product/${s.id}`); setThumb(0); }} className="w-28 flex-shrink-0 bg-white rounded-xl overflow-hidden border border-blush-soft text-left">
                  <div className="aspect-square"><img src={s.image} alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-2">
                    <div className="text-[10px] font-medium line-clamp-1">{s.name}</div>
                    <div className="text-brand font-bold text-[10px] mt-0.5">{formatIDR(s.price)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-[57px] inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-blush-soft">
        <div className="max-w-2xl mx-auto flex">
          <a data-testid="detail-chat-btn" href={`https://wa.me/${WA_CS}?text=${encodeURIComponent(`Halo Kiyana, saya mau tanya produk ${p.name}`)}`} target="_blank" rel="noreferrer"
            className="w-14 flex flex-col items-center justify-center py-2 text-teal-700 border-r border-blush-soft">
            <MessageCircle className="w-5 h-5" />
            <span className="text-[9px]">Chat</span>
          </a>
          <button data-testid="detail-add-cart-btn" onClick={() => { addToCart({ ...p, price: effPrice }, qty, `${color}, ${size}`); toast.success("Ditambahkan ke keranjang"); }}
            className="w-14 flex flex-col items-center justify-center py-2 text-teal-700 border-r border-blush-soft">
            <ShoppingCart className="w-5 h-5" />
            <span className="text-[9px]">+Keranjang</span>
          </button>
          <button data-testid="detail-buy-now-btn" onClick={buyNow}
            className="flex-1 bg-brand text-white font-semibold text-sm py-3 px-3 text-center leading-tight">
            {claimedV ? "Beli Dengan Voucher" : "Beli Sekarang"}
            <span className="block text-[11px] font-normal opacity-90">{formatIDR(displayTotal)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
